import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Manages version save/restore/delete, auto-save interval, and Cmd+S handler.
 */
export function useAutoSave({ getQuestionnaireData, restoreFullState, showNotification }) {
  const [versions, setVersions] = useState([]);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const saveIntervalRef = useRef(null);

  // Save current version to localStorage
  const saveVersion = useCallback(() => {
    const data = getQuestionnaireData();
    const timestamp = new Date().toISOString();
    const version = {
      id: Date.now(),
      timestamp,
      data
    };

    setVersions(prev => {
      const updated = [...prev, version];
      const trimmed = updated.slice(-5);
      localStorage.setItem('questionnaire_versions', JSON.stringify(trimmed));
      return trimmed;
    });

    setLastSaveTime(timestamp);
    localStorage.setItem('questionnaire_last_save', timestamp);
    showNotification('Automatisch gespeichert', 'success');
  }, [getQuestionnaireData, showNotification]);

  // Manual save with Cmd+S
  const handleManualSave = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      saveVersion();
    }
  }, [saveVersion]);

  // Restore version
  const restoreVersion = (versionId) => {
    const version = versions.find(v => v.id === versionId);
    if (!version || !version.data) return;
    restoreFullState(version.data);
    showNotification('Version wiederhergestellt', 'success');
  };

  // Delete a specific version
  const deleteVersion = (versionId) => {
    setVersions(prev => {
      const updated = prev.filter(v => v.id !== versionId);
      localStorage.setItem('questionnaire_versions', JSON.stringify(updated));
      return updated;
    });
    showNotification('Version gelöscht', 'success');
  };

  // Load versions from localStorage on mount
  useEffect(() => {
    const loadVersions = () => {
      try {
        const saved = localStorage.getItem('questionnaire_versions');
        if (saved) {
          const versionsData = JSON.parse(saved);
          setVersions(versionsData);
        }

        const lastSave = localStorage.getItem('questionnaire_last_save');
        if (lastSave) {
          setLastSaveTime(lastSave);
        }
      } catch (error) {
        console.error('Error loading versions:', error);
      }
    };

    loadVersions();
  }, []);

  // Auto-save every 2 minutes
  useEffect(() => {
    saveIntervalRef.current = setInterval(() => {
      saveVersion();
    }, 120000);

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
  }, [saveVersion]);

  // Keyboard shortcut for manual save
  useEffect(() => {
    document.addEventListener('keydown', handleManualSave);
    return () => {
      document.removeEventListener('keydown', handleManualSave);
    };
  }, [handleManualSave]);

  return {
    versions,
    lastSaveTime,
    selectedVersionId, setSelectedVersionId,
    saveVersion,
    restoreVersion,
    deleteVersion
  };
}
